import type { CurrentUser } from '@/api/modules/account'
import { clearSession, getAccessToken, subscribeSession } from '@/api/client'
import { accountApi } from '@/api/modules/account'
import router from '@/router'
import { generateAccountAvatar } from '@/utils/account-avatar'
import { redirectToAccountCenter } from '@/utils/account-center'

export const useAppAccountStore = defineStore('appAccount', () => {
  const appSettingsStore = useAppSettingsStore()
  const appTabbarStore = useAppTabbarStore()
  const appRouteStore = useAppRouteStore()
  const appMenuStore = useAppMenuStore()

  // 账号信息
  const token = ref(getAccessToken())
  const account = ref(localStorage.getItem('account') ?? '当前账户')
  const userNo = ref(localStorage.getItem('userNo') ?? '')
  const avatar = computed(() => userNo.value ? generateAccountAvatar(userNo.value) : localStorage.getItem('avatar') ?? '')
  let accountProfileRequest: Promise<void> | undefined

  // 权限信息
  const permissions = ref<string[]>([])
  let sessionClearRedirect: string | undefined

  subscribeSession((nextToken) => {
    token.value = nextToken
    if (nextToken) {
      return
    }

    logoutCleanStatus()
    redirectToAccountCenter(sessionClearRedirect ?? router.currentRoute.value.fullPath)
    sessionClearRedirect = undefined
  })

  // 登录状态
  const isLogin = computed(() => {
    if (token.value) {
      return true
    }
    return false
  })

  // 登录
  async function login(_data?: unknown) {
    redirectToAccountCenter(router.currentRoute.value.fullPath)
  }

  // 手动登出
  function logout(redirect = router.currentRoute.value.fullPath) {
    sessionClearRedirect = redirect
    clearSession()
  }

  // 请求登出
  function requestLogout() {
    sessionClearRedirect = router.currentRoute.value.fullPath
    clearSession()
  }

  // 登出后清除状态
  function logoutCleanStatus() {
    localStorage.removeItem('account')
    localStorage.removeItem('avatar')
    localStorage.removeItem('userNo')
    account.value = ''
    userNo.value = ''
    permissions.value = []
    appSettingsStore.updateSettings({}, true)
    appTabbarStore.clean()
    appRouteStore.removeRoutes()
    appMenuStore.setActived(0)
  }

  // 获取权限
  async function getPermissions() {
    permissions.value = []
  }

  function syncAccountProfile(profile: Pick<CurrentUser, 'email' | 'phone' | 'userNo'>) {
    const name = profile.email || profile.phone || profile.userNo
    account.value = name
    userNo.value = profile.userNo
    localStorage.setItem('account', name)
    localStorage.setItem('userNo', profile.userNo)
  }

  async function ensureAccountProfile() {
    if (userNo.value) {
      return
    }
    if (!accountProfileRequest) {
      accountProfileRequest = accountApi.me()
        .then(syncAccountProfile)
        .catch(() => undefined)
        .finally(() => {
          accountProfileRequest = undefined
        })
    }
    await accountProfileRequest
  }

  // 修改密码
  async function editPassword(_data?: unknown) {
    redirectToAccountCenter('/account/')
  }

  return {
    token,
    account,
    userNo,
    avatar,
    permissions,
    isLogin,
    login,
    logout,
    requestLogout,
    getPermissions,
    ensureAccountProfile,
    syncAccountProfile,
    editPassword,
  }
})
